import type { UseMutationOptions, UseMutationResult } from "react-query";
import { useMutation } from "react-query";
import { useToast } from "@commonlibs";
import useTranslate from "ui/locale/hooks/useTranslate";
import downloadFile from "ui/common/utils/downloadFile";
import DomainError from "domain/error/DomainError";
import DomainErrorCodes from "domain/error/DomainErrorCodes";
import { giftcardsApiV2 } from "../api/giftcardsApiV2";

export interface UseExportGiftCardStockParams extends Record<string, unknown> {
    partnerAddress: string;
}

export default function useExportGiftCardStock(
    options?: UseMutationOptions<void, unknown, UseExportGiftCardStockParams>,
): UseMutationResult<void, unknown, UseExportGiftCardStockParams> {
    const translate = useTranslate();
    const translateError = useTranslate("error");
    const { showToast } = useToast();

    const { onError: onErrorOption, onSuccess: onSuccessOption, ...restOptions } = options ?? {};

    return useMutation(
        async ({ partnerAddress }: UseExportGiftCardStockParams) => {
            try {
                const res = await giftcardsApiV2.exportGiftCardStock({ partnerAddress });
                downloadFile(res.data, "gift_cards_stock_report", "csv");
            } catch (error) {
                // Blob responseType causes HTTP errors to arrive as raw AxiosError (not DomainError)
                // because the interceptor can't parse a Blob as JSON. We fall back to status code.
                showExportErrorToast(error);
                throw error;
            }
        },
        {
            onSuccess: (data, variables, context) => {
                showToast(translate("transactionsExportSuccess"), { type: "success" });
                onSuccessOption?.(data, variables, context);
            },
            onError: (_error, variables, context) => {
                onErrorOption?.(_error, variables, context);
            },
            ...restOptions,
        },
    );

    function showExportErrorToast(error: unknown) {
        if (error instanceof DomainError) {
            if (error.code === DomainErrorCodes.PARTNER_NOT_FOUND || error.code === DomainErrorCodes.NOT_FOUND) {
                showToast(translateError("partnerNotFound"), { type: "warning" });
            } else if (error.code === DomainErrorCodes.FORBIDDEN) {
                showToast(translateError("forbidden"), { type: "warning" });
            } else {
                showToast(translateError("somethingWentWrong"), { type: "error" });
            }
            return;
        }

        const status = (error as { response?: { status?: number } }).response?.status;
        if (status === 404) {
            showToast(translateError("partnerNotFound"), { type: "warning" });
        } else if (status === 403 || status === 401) {
            showToast(translateError("forbidden"), { type: "warning" });
        } else {
            showToast(translateError("somethingWentWrong"), { type: "error" });
        }
    }
}
